import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import type { CoworkMessage } from '../../types/cowork';
import { i18nService } from '../../services/i18n';
import { getResultOpinionTags, setResultFeedback, submitResultOpinion, type ResultFeedback } from '../../services/resultFeedback';

/** 复制公开结果；兼容HTTP环境的剪贴板回退。 */
export const CopyButton: React.FC<{
  content: string;
  visible: boolean;
}> = ({ content, visible }) => {
  const [copied, setCopied] = useState(false);

  /** 优先使用剪贴板API，HTTP环境继续复用已有兼容路径。 */
  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // HTTP非安全上下文使用兼容复制路径。
      try {
        const textarea = document.createElement('textarea');
        textarea.value = content;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <button
      onClick={handleCopy}
      className={`p-1.5 rounded-md dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-all duration-200 ${
        visible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      title={i18nService.t('copyToClipboard')}
    >
      {copied ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="w-4 h-4 text-green-500"
          aria-hidden="true"
        >
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="w-4 h-4 text-[var(--icon-secondary)]"
          aria-hidden="true"
        >
          <rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect>
          <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path>
        </svg>
      )}
    </button>
  );
};

/** 主回答评价与意见面板，等待服务端确认后才更新选中状态。 */
export const ResultFeedbackButtons: React.FC<{ sessionId: string; message: CoworkMessage; desktop: boolean }> = ({ sessionId, message, desktop }) => {
  const selected = (message.metadata?.resultFeedback || null) as ResultFeedback;
  const [busy, setBusy] = useState(false);
  // React状态更新前同一事件周期可能再次点击，用同步锁保证只发送一次变更。
  const mutationLocked = useRef(false);
  const tagGeneration = useRef(0);
  useEffect(() => () => { tagGeneration.current++; }, []);
  const [opinionOpen, setOpinionOpen] = useState(false);
  const [opinionPreviousFeedback, setOpinionPreviousFeedback] = useState<ResultFeedback>(null);
  const [opinionVoteCommitted, setOpinionVoteCommitted] = useState(false);
  const [tags, setTags] = useState<Array<{ code: string; name: string }>>([]);
  const [tagCodes, setTagCodes] = useState<string[]>([]);
  const [customOpinion, setCustomOpinion] = useState(false);
  const [content, setContent] = useState('');
  const [error, setError] = useState('');
  const downvoteButtonRef = useRef<HTMLButtonElement>(null);
  const opinionPanelRef = useRef<HTMLDivElement>(null);
  const [opinionPosition, setOpinionPosition] = useState({ left: 8, top: 8 });

  useLayoutEffect(() => {
    if (!opinionOpen) return;
    /** 面板跟随评价按钮，窄屏和滚动时限制在可见窗口内。 */
    const updatePosition = () => {
      const anchor = downvoteButtonRef.current;
      const panel = opinionPanelRef.current;
      if (!anchor || !panel) return;
      const rect = anchor.getBoundingClientRect();
      const width = Math.min(320, window.innerWidth - 16);
      const height = panel.offsetHeight;
      const above = rect.top - height - 8;
      setOpinionPosition({
        left: Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8)),
        top: Math.max(8, Math.min(above >= 8 ? above : rect.bottom + 8, window.innerHeight - height - 8)),
      });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updatePosition);
    if (opinionPanelRef.current) observer?.observe(opinionPanelRef.current);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      observer?.disconnect();
    };
  }, [opinionOpen, tags, customOpinion]);

  useLayoutEffect(() => {
    if (!opinionOpen) return;
    const previousFocus = document.activeElement;
    opinionPanelRef.current?.focus();
    return () => {
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
    };
  }, [opinionOpen]);

  /** 关闭未提交意见不写服务端；桌面部分成功时需先确认撤销。 */
  const closeOpinion = async () => {
    if (mutationLocked.current || busy) return;
    mutationLocked.current = true;
    tagGeneration.current++;
    setBusy(true);
    setError('');
    try {
      if (opinionVoteCommitted) {
        await setResultFeedback(sessionId, message.id, Number(message.timestamp), opinionPreviousFeedback, desktop);
      }
      setOpinionOpen(false);
      setOpinionVoteCommitted(false);
      setTagCodes([]);
      setCustomOpinion(false);
      setContent('');
    } catch {
      setError(i18nService.t('coworkOpinionCancelFailed'));
    } finally {
      mutationLocked.current = false;
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!opinionOpen) return;
    /** 面板内循环键盘焦点，Escape沿同一取消流程处理。 */
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') void closeOpinion();
      if (event.key !== 'Tab' || !opinionPanelRef.current) return;
      const focusable = Array.from(opinionPanelRef.current.querySelectorAll<HTMLElement>('button:not(:disabled), textarea:not(:disabled)'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === opinionPanelRef.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [opinionOpen, busy, opinionVoteCommitted, opinionPreviousFeedback, sessionId, message.id, message.timestamp, desktop]);

  /** 点赞可撤销；点踩先收集意见，提交前不伪装成已评价。 */
  const rate = async (quality: Exclude<ResultFeedback, null>) => {
    if (mutationLocked.current || busy || opinionOpen) return;
    const next = selected === quality ? null : quality;
    if (next === 'not_helpful') {
      setError('');
      setOpinionPreviousFeedback(selected);
      setOpinionVoteCommitted(false);
      setTagCodes([]);
      setCustomOpinion(false);
      setContent('');
      setTags([]);
      setOpinionOpen(true);
      const generation = ++tagGeneration.current;
      void getResultOpinionTags(sessionId, desktop)
        .then(value => { if (generation === tagGeneration.current) setTags(value); })
        .catch(() => { if (generation === tagGeneration.current) setTags([]); });
      return;
    }
    mutationLocked.current = true;
    setBusy(true);
    setError('');
    try {
      await setResultFeedback(sessionId, message.id, Number(message.timestamp), next, desktop);
      setOpinionOpen(false);
    } catch {
      setError(i18nService.t('coworkFeedbackFailed'));
    } finally {
      mutationLocked.current = false;
      setBusy(false);
    }
  };

  /** 结构化意见失败保留输入，桌面双步骤失败需恢复原投票。 */
  const sendOpinion = async () => {
    if (mutationLocked.current || busy || (!tagCodes.length && !customOpinion)) return;
    mutationLocked.current = true;
    setBusy(true);
    setError('');
    let voteCommitted = opinionVoteCommitted;
    try {
      if (desktop && !voteCommitted) {
        await setResultFeedback(sessionId, message.id, Number(message.timestamp), 'not_helpful', desktop);
        voteCommitted = true;
        setOpinionVoteCommitted(true);
      }
      await submitResultOpinion(sessionId, message.id, Number(message.timestamp), tagCodes,
        content.trim() || (customOpinion ? i18nService.t('coworkOpinionOther') : ''), desktop);
      setOpinionOpen(false);
      setOpinionVoteCommitted(false);
    } catch {
      if (desktop && voteCommitted) {
        try {
          await setResultFeedback(sessionId, message.id, Number(message.timestamp), opinionPreviousFeedback, desktop);
          setOpinionVoteCommitted(false);
        } catch {
          setError(i18nService.t('coworkOpinionCancelFailed'));
          return;
        }
      }
      setError(i18nService.t('coworkFeedbackFailed'));
    } finally {
      mutationLocked.current = false;
      setBusy(false);
    }
  };

  return <>
    {(['helpful', 'not_helpful'] as const).map(quality => {
      const Icon = quality === 'helpful' ? ThumbsUp : ThumbsDown;
      const label = i18nService.t(quality === 'helpful' ? 'coworkResultHelpful' : 'coworkResultNotHelpful');
      return <button key={quality} ref={quality === 'not_helpful' ? downvoteButtonRef : undefined}
        type="button" title={label} aria-label={label} aria-pressed={selected === quality} disabled={busy}
        onClick={() => void rate(quality)}
        className={`p-1.5 rounded-md transition-colors hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover disabled:opacity-50 ${selected === quality ? 'text-claude-accent bg-claude-accent/10' : 'text-[var(--icon-secondary)]'}`}>
        <Icon aria-hidden="true" className="h-4 w-4" />
      </button>;
    })}
    {error && !opinionOpen && <span role="alert" className="text-xs text-red-500">{error}</span>}
    {opinionOpen && createPortal(<>
      <div className="fixed inset-0 z-[999] bg-transparent" onMouseDown={() => void closeOpinion()} aria-hidden="true" />
      <div ref={opinionPanelRef} role="dialog" aria-modal="true" tabIndex={-1}
        aria-label={i18nService.t('coworkOpinionTitle')}
        className="fixed z-[1000] max-h-[calc(100vh-16px)] overflow-y-auto rounded-md border border-claude-border bg-white p-3 shadow-xl dark:border-claude-darkBorder dark:bg-claude-darkSurface"
        style={{ left: opinionPosition.left, top: opinionPosition.top, width: 'min(320px, calc(100vw - 16px))' }}>
        <div className="mb-2.5 flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">{i18nService.t('coworkOpinionTitle')}</h2>
          <button type="button" title={i18nService.t('cancel')} aria-label={i18nService.t('cancel')} disabled={busy}
            onClick={() => void closeOpinion()} className="rounded-md p-1"><XMarkIcon className="h-4 w-4" /></button>
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {tags.map(tag => <button key={tag.code} type="button" aria-pressed={tagCodes.includes(tag.code)}
            onClick={() => setTagCodes(current => current.includes(tag.code) ? current.filter(code => code !== tag.code) : [...current, tag.code])}
            className={`min-h-9 rounded border px-1 text-xs ${tagCodes.includes(tag.code) ? 'border-claude-accent text-claude-accent' : 'border-claude-border dark:border-claude-darkBorder'}`}>{tag.name}</button>)}
          <button type="button" aria-pressed={customOpinion}
            onClick={() => { setCustomOpinion(!customOpinion); if (customOpinion) setContent(''); }}
            className={`min-h-9 rounded border px-1 text-xs ${customOpinion ? 'border-claude-accent text-claude-accent' : 'border-claude-border dark:border-claude-darkBorder'}`}>{i18nService.t('coworkOpinionOther')}</button>
        </div>
        {customOpinion && <div className="relative mt-3">
          <textarea value={content} onChange={event => setContent(event.target.value)} maxLength={200} rows={3}
            aria-label={i18nService.t('coworkOpinionPlaceholder')} placeholder={i18nService.t('coworkOpinionPlaceholder')}
            className="w-full resize-y rounded-md border border-claude-border bg-transparent px-2 pb-6 pt-2 text-xs dark:border-claude-darkBorder" />
          <span className="pointer-events-none absolute bottom-2 right-3 text-[11px] text-[var(--ui-text-secondary)]">{content.length}/200</span>
        </div>}
        {error && <p role="alert" className="mt-2 text-xs text-red-500">{error}</p>}
        <button type="button" disabled={busy || (!tagCodes.length && !customOpinion)} onClick={() => void sendOpinion()}
          className="mt-3 w-full rounded-md bg-claude-accent px-3 py-2 text-xs font-medium text-white disabled:opacity-50">{i18nService.t('coworkOpinionSubmit')}</button>
      </div>
    </>, document.body)}
  </>;
};
